import {
  CloseCircleOutlined,
  DollarOutlined,
  SyncOutlined,
  HistoryOutlined,
  FileTextOutlined,
  RollbackOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { Button, Modal } from "antd";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getTodaysInvoicesByType,
  getTodaysPendingInvoices,
  getInvoicesByType,
} from "../../services/invoiceService";
import AperturaDeCajaForm from "../AperturaDeCaja/AperturaDeCajaForm";
import { addAperturaDeCaja } from "../../services/aperturaDeCajaService";
import CierreDeCajaForm from "../CierreDeCaja/CierreDeCajaForm";
import TraerHistoricosForm from "../SincronizarHistoricos/TraerHistoricosForm";
import { useConfig } from "../../contexts/ConfigContext";
import { useAuth } from "../../contexts/AuthContext";
import { pantallaVedadaParaCajero } from "../../config/rbac/rbac.config";
import { hayCajaAbierta } from "../../services/cajaEstadoService";
import "./Dashboard.css";
import { getDashboardConfig } from "./dashboard.config";
import PageSpinner from "../Spinner/PageSpinner";
import DashboardMetrics from "./DashboardMetrics";
import {
  getDashboardMetrics,
  getRangeBounds,
  getUltimaAperturaYCierre,
} from "../../services/dashboardService";
import { formatNumberByCountry } from "../../utils/formatNumbers";
import dayjs from "dayjs";
import { useIsMobile } from "../../hooks/useIsMobile";
import { TituloConAyuda } from "../Layout/AyudaDePantalla";

const DIAS = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
const MESES = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];

// Fecha larga en español (ej. "Miércoles 12 de agosto, 2026"), sin depender
// del locale global de dayjs (no está configurado en la app).
const formatLongDateEs = () => {
  const today = dayjs();
  return `${DIAS[today.day()]} ${today.date()} de ${MESES[today.month()]}, ${today.year()}`;
};

// Conteo entero (sin moneda) con separador de miles del país.
const count = (value, country) =>
  country ? formatNumberByCountry(value || 0, country, false, 0) : String(value ?? 0);

const Dashboard = () => {
  const { country, loading } = useConfig();
  const { currentUser } = useAuth();
  // Los atajos del tablero siguen la misma tabla que el menú: no tiene sentido
  // ofrecer un acceso directo a una pantalla en la que después se rebota.
  const sinAbonos = pantallaVedadaParaCajero("abonos", country?.code, currentUser?.Admin);
  const sinPendientes = pantallaVedadaParaCajero(
    "pendientesDeSincronizar",
    country?.code,
    currentUser?.Admin
  );
  const { showAbonos, showHistoricoAbonos } = getDashboardConfig(country?.code);
  const [refreshKey, setRefreshKey] = useState(0);
  const [invoicesByType, setInvoicesByType] = useState({
    invoices: [],
    creditMemos: [],
    pending: [],
  });

  const [historicalData, setHistoricalData] = useState({
    allInvoices: [],
    allCreditMemos: [],
  });

  const [range, setRange] = useState("today");
  const [customRange, setCustomRange] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [shiftInfo, setShiftInfo] = useState(null);
  const [cajaAbierta, setCajaAbierta] = useState(false);

  const [aperturaCajaModalVisible, setAperturaCajaModalVisible] = useState(false);
  const [loadingAperturaForm, setLoadingAperturaForm] = useState(false);
  const [aperturaFormError, setAperturaFormError] = useState("");
  const [cierreCajaModalVisible, setCierreCajaModalVisible] = useState(false);
  const [traerHistoricosModalVisible, setTraerHistoricosModalVisible] = useState(false);
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const handleHistoricosSynced = async () => {
    try {
      // vuelves a cargar desde IndexedDB usando tu servicio
      const allInvoices = await getInvoicesByType("Invoice");
      const allCreditMemos = await getInvoicesByType("Credit Memo");

      setHistoricalData({
        allInvoices,
        allCreditMemos,
      });
    } catch (error) {
      console.error("Error al refrescar datos históricos:", error);
    }
  };

  // Refleja si hay una caja abierta en este dispositivo (lee de Dexie, no de
  // estado en memoria) -- así el botón "Abrir Caja" queda deshabilitado
  // aunque se cierre y reabra el navegador con una caja ya abierta.
  const checkCajaAbierta = async () => {
    const abierta = await hayCajaAbierta();
    setCajaAbierta(abierta);
  };

  useEffect(() => {
    checkCajaAbierta();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Obtener facturas de hoy por tipo
        const regularInvoices = await getTodaysInvoicesByType("Invoice");
        const creditMemos = await getTodaysInvoicesByType("Credit Memo");
        const pendingInvoices = await getTodaysPendingInvoices();

        setInvoicesByType({
          invoices: regularInvoices,
          creditMemos: creditMemos,
          pending: pendingInvoices,
        });

        // Obtener datos históricos (todas las facturas)
        handleHistoricosSynced();
      } catch (error) {
        console.error("Error al cargar el dashboard:", error);
      }
    };

    fetchData();
  }, [refreshKey]);

  useEffect(() => {
    if (!country) return;

    const fetchMetrics = async () => {
      try {
        const { start, end } = getRangeBounds(range, customRange);
        const [metricsResult, shiftResult] = await Promise.all([
          getDashboardMetrics(start, end, country),
          getUltimaAperturaYCierre(),
        ]);
        setMetrics(metricsResult);
        setShiftInfo(shiftResult);
      } catch (error) {
        console.error("Error al calcular métricas del dashboard:", error);
      }
    };

    fetchMetrics();
  }, [country, range, customRange, refreshKey]);

  const handleCardClick = () => {
    navigate("/facturas?filter=today");
  };

  const handleCreditMemoCardClick = () => {
    navigate("/abonos?filter=today");
  };

  const handlePendingCardClick = () => {
    navigate("/facturas-pendientes");
  };

  const handleHistoricalInvoicesClick = () => {
    navigate("/facturas");
  };

  const handleHistoricalCreditMemosClick = () => {
    navigate("/abonos");
  };

  const showAperturaCajaModal = () => {
    setAperturaFormError("");
    setAperturaCajaModalVisible(true);
  };

  const handleAperturaCajaModalClose = (form) => {
    if (form && typeof form.resetFields === "function") form.resetFields();
    setAperturaCajaModalVisible(false);
  };

  const handleSubmitAperturaForm = async (aperturaData) => {
    try {
      setAperturaFormError("");
      setLoadingAperturaForm(true);
      await addAperturaDeCaja(aperturaData);
      window.messageApi.success("Apertura de caja registrada correctamente");
      setAperturaCajaModalVisible(false);
      setRefreshKey((prev) => prev + 1);
      checkCajaAbierta();
    } catch (error) {
      setAperturaFormError(error.message || "Error al procesar la apertura de caja");
      console.error("Error al procesar apertura de caja:", error);
    } finally {
      setLoadingAperturaForm(false);
    }
  };

  const showCierreCajaModal = () => {
    setCierreCajaModalVisible(true);
  };

  const handleCierreCajaModalClose = () => {
    setCierreCajaModalVisible(false);
  };

  const handleCierreAdded = (result) => {
    // Cerrar el modal después de registrar exitosamente
    setCierreCajaModalVisible(false);
    checkCajaAbierta();
  };

  const handleTraerHistoricosModal = (open) => {
    setTraerHistoricosModalVisible(open);
  };

  return (
    <div className="dashboard-container">
      {loading ? (
        <PageSpinner />
      ) : (
        <>
          <div className="ops-bar">
            <div>
              <h1>Bienvenido a ExceliaOne</h1>
              <p className="page-head__sub">{formatLongDateEs()}</p>
            </div>
            <div className="ops-actions">
              {/* Los dos botones son excluyentes: sin caja abierta no hay nada que
                  cerrar, y con una abierta no se puede abrir otra. Antes sólo el de
                  abrir lo sabía, así que «Cerrar caja» invitaba a pulsarlo para
                  después decir que no había caja. */}
              <Button
                type="primary"
                icon={<DollarOutlined />}
                onClick={showAperturaCajaModal}
                className="open-cash-button"
                disabled={cajaAbierta}
                title={cajaAbierta ? "Ya hay una caja abierta" : undefined}
              >
                Abrir caja
              </Button>
              <Button
                type="primary"
                icon={<CloseCircleOutlined />}
                onClick={showCierreCajaModal}
                className="close-cash-button"
                disabled={!cajaAbierta}
                title={!cajaAbierta ? "No hay ninguna caja abierta" : undefined}
              >
                Cerrar caja
              </Button>
              {/* Lo usan los dos roles: el cajero necesita recuperar sus propios
                  documentos de una jornada anterior. */}
              <Button
                type="primary"
                icon={<HistoryOutlined />}
                className="sync-history-button"
                onClick={() => {
                  handleTraerHistoricosModal(true);
                }}
              >
                Traer históricos
              </Button>
            </div>
          </div>

          <div className="quick-row">
            <div className="quick-card" style={{ "--qc": "#00828b" }} onClick={handleCardClick}>
              <div className="quick-card__icon">
                <FileTextOutlined />
              </div>
              <div>
                <div className="quick-card__value">
                  {count(invoicesByType.invoices.length, country)}
                </div>
                <div className="quick-card__label">Facturas hoy</div>
              </div>
              <RightOutlined className="quick-card__arrow" />
            </div>

            {showAbonos && !sinAbonos && (
              <div
                className="quick-card"
                style={{ "--qc": "#00828b" }}
                onClick={handleCreditMemoCardClick}
              >
                <div className="quick-card__icon">
                  <RollbackOutlined />
                </div>
                <div>
                  <div className="quick-card__value">
                    {count(invoicesByType.creditMemos.length, country)}
                  </div>
                  <div className="quick-card__label">Abonos hoy</div>
                </div>
                <RightOutlined className="quick-card__arrow" />
              </div>
            )}

            {!sinPendientes && (
              <div
                className="quick-card"
                style={{ "--qc": "#fa8c16" }}
                onClick={handlePendingCardClick}
              >
                <div className="quick-card__icon">
                  <SyncOutlined />
                </div>
                <div>
                  <div className="quick-card__value">
                    {count(invoicesByType.pending.length, country)}
                  </div>
                  <div className="quick-card__label">Pendientes de sinc.</div>
                </div>
                <RightOutlined className="quick-card__arrow" />
              </div>
            )}

            <div
              className="quick-card"
              style={{ "--qc": "#722ed1" }}
              onClick={handleHistoricalInvoicesClick}
            >
              <div className="quick-card__icon">
                <HistoryOutlined />
              </div>
              <div>
                <div className="quick-card__value">
                  {count(historicalData.allInvoices.length, country)}
                </div>
                <div className="quick-card__label">Histórico facturas</div>
              </div>
              <RightOutlined className="quick-card__arrow" />
            </div>

            {showHistoricoAbonos && !sinAbonos && (
              <div
                className="quick-card"
                style={{ "--qc": "#722ed1" }}
                onClick={handleHistoricalCreditMemosClick}
              >
                <div className="quick-card__icon">
                  <HistoryOutlined />
                </div>
                <div>
                  <div className="quick-card__value">
                    {count(historicalData.allCreditMemos.length, country)}
                  </div>
                  <div className="quick-card__label">Histórico abonos</div>
                </div>
                <RightOutlined className="quick-card__arrow" />
              </div>
            )}
          </div>

          {metrics ? (
            <DashboardMetrics
              metrics={metrics}
              shiftInfo={shiftInfo}
              country={country}
              range={range}
              onRangeChange={setRange}
              customRange={customRange}
              onCustomRangeChange={setCustomRange}
            />
          ) : (
            <PageSpinner />
          )}

          {/* Modal para el formulario de apertura de caja */}
          <Modal
            title={<TituloConAyuda titulo="Apertura de caja" ayuda="apertura-de-caja" />}
            open={aperturaCajaModalVisible}
            onCancel={handleAperturaCajaModalClose}
            footer={null}
            width={isMobile ? "95%" : 700}
            destroyOnHidden
          >
            <AperturaDeCajaForm
              initialData={null}
              isCreate={true}
              loadingForm={loadingAperturaForm}
              error={aperturaFormError}
              handleSubmit={handleSubmitAperturaForm}
              handleCancel={handleAperturaCajaModalClose}
              handleError={setAperturaFormError}
              onRefreshDashboard={() => setRefreshKey((prev) => prev + 1)}
            />
          </Modal>

          {/* Modal para el formulario de cierre de caja */}
          <Modal
            title={<TituloConAyuda titulo="Cierre de caja" ayuda="cierre-de-caja" />}
            open={cierreCajaModalVisible}
            onCancel={handleCierreCajaModalClose}
            footer={null}
            width={isMobile ? "95%" : 700}
            destroyOnHidden
          >
            <CierreDeCajaForm onSave={handleCierreAdded} onCancel={handleCierreCajaModalClose} />
          </Modal>

          {/* Modal para el formulario de traer historicos */}
          <Modal
            title={<TituloConAyuda titulo="Traer históricos" ayuda="traer-historicos" />}
            open={traerHistoricosModalVisible}
            onCancel={() => {
              setTraerHistoricosModalVisible(false);
            }}
            footer={null}
            width={isMobile ? "95%" : 700}
            destroyOnHidden
          >
            <TraerHistoricosForm
              handleCancel={() => {
                setTraerHistoricosModalVisible(false);
              }}
              handleHistoricosSynced={handleHistoricosSynced}
              onRefreshDashboard={() => setRefreshKey((prev) => prev + 1)}
              country={country}
            />
          </Modal>
        </>
      )}
    </div>
  );
};

export default Dashboard;
